import React, { useState } from "react";
import { DateTimeUtils, Person, PRODUCT_PERMISSIONS } from "dyl-components";
import { useDispatch, useSelector } from "react-redux";
import { VALIDATORS } from "dyl-components";
import { Controller, useController, useFormContext } from "react-hook-form";
import {
    Dropdown,
    Form,
    Grid,
    Icon,
    Label,
    List,
    ListItem,
    Select,
} from "semantic-ui-react";
import { STATES } from "shared/constants/STATES";
import UserSearchField from "shared/forms/UserSearchField";
import { PhoneUtil } from "utils";
import contactDuplicatesActions from 'actions/contact_duplicates';
import "./UserForm.scss";
import { Link } from "react-router-dom";
import usersActions from "actions/user"
import RestrictedView from "shared/RestrictedView";

const UserForm = ({ customer, contact_id, canAddUserExtension, reportsToDisabled }) => {
    const { unusedUserExtensions, isReadingUnusedUserExtensions, isDyl, userPermissionsIsOwner } = useSelector((state) => {
        return {
            unusedUserExtensions: state.pbx_config.unusedUserExtensions,
            isReadingUnusedUserExtensions: state.pbx_config.isReadingUnusedUserExtensions,
            isDyl: state.auth.dyl_employee,
            userPermissionsIsOwner: state.role.userPermissions?.is_owner_role
    }});

    const {
        roles,
        contactOptions,
        teams,
        isReadingTeams,
        isReadingRoles,
        isFindingUserByEmail,
    } = useSelector((state) => ({
        isFindingUserByEmail: state.user.isFindingUserByEmail,
        isCreating:
            state.users.isCreatingUser ||
            state.auth.isCreatingUser ||
            state.team.teamWhereUsersAreBeingAdded ||
            state.company.isCreatingCompany,
        roles: state.roles.rolesNames
            .filter(role => {
                const owner = role?.is_owner_role;
                const adminOwner = role?.is_administrative_owner_role;
                if (customer){
                    return owner;
                }
                if(isDyl && userPermissionsIsOwner){
                    return !owner;
                }
                return !owner && !adminOwner;
            })
            .map(({ access_role_id, access_role_name }) => ({
                key: access_role_id,
                value: access_role_id,
                text: access_role_name,
            })),
        isReadingRoles: state.roles.isReadingRoles,
        teams: state.teams.teams
            .filter(({ deleted }) => !deleted)
            .map(({ id, name }) => ({
                key: id,
                value: id,
                text: name,
            })),
        isReadingTeams: state.teams.isReadingTeams,
        contactOptions: state.account.contactsForPinning.map((contact) => {
            const { first_name, last_name, phone, email, location } = contact;
            const name = `${first_name} ${last_name}`;

            return {
                key: contact.id,
                value: contact.id,
                text: name,
                content: <Person username={name} email="" isUsernameOnly />,
                first_name,
                last_name,
                phone: phone.phone,
                email: email.email,
                location,
            };
        }),
    }));

    const dispatch = useDispatch();
    const [focusedPhones, setFocusedPhones] = useState([]);

    const addFocus = (type) => {
        setFocusedPhones([...focusedPhones, type]);
    };
    const removeFocus = (type) => {
        setFocusedPhones(focusedPhones.filter(phone => phone !== type));
    }
    const isFocused = (type) => {
        return focusedPhones.includes(type);
    }
    const { control, setError, reset } = useFormContext();

    const { field: contactIdField } = useController({
        control,
        name: "contact_id",
    });

    const contactField = (() => {
        if (!customer) {
            return null;
        }
    const content = contactOptions.find(
        ({ value }) => value === contactIdField.value
    )?.content;
        return (
            <Grid.Row>
                <Grid.Column>
                    <ListItem
                        header={
                            <b>
                                Contact Name{" "}
                                <Icon size="tiny" name="asterisk" color="red" />
                            </b>
                        }
                        description={
                            <Form.Dropdown
                                placeholder="Select Contact"
                                selectOnBlur={false}
                                selection
                                options={contactOptions}
                                name="contact_id"
                                onChange={(_, { value }) => {
                                    contactIdField.onChange({
                                        target: {
                                            name: contactIdField.name,
                                            value,
                                        },
                                    });
                                    const contact = contactOptions.find(
                                        (contact) => contact.value === value
                                    );
                                    reset({
                                        cell_number: contact.phone,
                                        email: contact.email,
                                        first_name: contact.first_name,
                                        last_name: contact.last_name,
                                        street: contact?.location?.street,
                                        suite: contact?.location
                                            ?.additional_street,
                                        city: contact?.location?.city,
                                        state: contact?.location?.state,
                                        zipcode: contact?.location?.zip,
                                    });
                                }}
                                text={content}
                                fluid
                                value={contactIdField.value}
                            />
                        }
                    />
                </Grid.Column>
            </Grid.Row>
        );
    })();

    const checkIfEmailAlreadyInUse = async ({ name, value }) => {
        if (value !== "" && (await VALIDATORS.EMAIL_ADDRESS().isValid(value))) {
            if (customer) {
                const duplicateEmails = await dispatch(contactDuplicatesActions.checkDuplicates({ emails: [value] }));
                const possibleDuplicate = duplicateEmails[0]?.contact_id;
                const hasNoDuplicates = possibleDuplicate === undefined || Number(possibleDuplicate) === Number(contact_id);
                if (!hasNoDuplicates) {
                    await setError(name, {
                        type: 'unique_email',
                        message: (
                            <Label color='red'>Email matches an <Link style={{ color: "#2B78FF" }} target={"_blank"} to={`/contact/${possibleDuplicate}`}>existing record</Link></Label>
                        )
                    });
                }
            }
            const { customer_id, user_id } = await dispatch(
                    usersActions.findByEmail("", {
                        email: value,
                    })
                );
                if (customer_id || user_id) {
                    await setError(name, {
                        type: 'unique_email',
                        message: (
                            <Label color='red'>Email already in use</Label>
                        )
                    });
                }
        }
    };

    return (
        <Grid>
            {contactField}
            <Grid.Row columns={2}>
                <Grid.Column>
                    <List relaxed size="small" as={Form}>
                        <ListItem
                            icon={<Icon name="user" color="black" />}
                            header={
                                <b>
                                    First Name{" "}
                                    <Icon
                                        size="tiny"
                                        name="asterisk"
                                        color="red"
                                    />
                                </b>
                            }
                            description={
                                <Controller
                                    control={control}
                                    name="first_name"
                                    render={({
                                        field: { name, value, onChange },
                                        fieldState: { error },
                                    }) => (
                                        <Form.Input
                                            value={value}
                                            onChange={(_, { value }) => {
                                                onChange({
                                                    target: {
                                                        name,
                                                        value,
                                                    },
                                                });
                                            }}
                                            name="first_name"
                                            error={error?.message}
                                            fluid
                                            required
                                        />
                                    )}
                                />
                            }
                        />
                        <ListItem
                            icon={<Icon />}
                            header={
                                <b>
                                    Last Name{" "}
                                    <Icon
                                        size="tiny"
                                        name="asterisk"
                                        color="red"
                                    />
                                </b>
                            }
                            description={
                                <Controller
                                    control={control}
                                    name="last_name"
                                    render={({
                                        field: { name, value, onChange },
                                        fieldState: { error },
                                    }) => (
                                        <Form.Input
                                            value={value}
                                            onChange={(_, { value }) => {
                                                onChange({
                                                    target: {
                                                        name,
                                                        value,
                                                    },
                                                });
                                            }}
                                            name="last_name"
                                            error={error?.message}
                                            fluid
                                            required
                                        />
                                    )}
                                />
                            }
                        />
                        <ListItem
                            icon={<Icon name="sign in" color="black" />}
                            header={
                                <b>
                                    Email{" "}
                                    <Icon
                                        size="tiny"
                                        name="asterisk"
                                        color="red"
                                    />
                                </b>
                            }
                            description={
                                <Controller
                                    control={control}
                                    name="email"
                                    render={({
                                        field: { name, value, onChange },
                                        fieldState: { error },
                                    }) => (
                                        <Form.Input
                                            value={value}
                                            required
                                            loading={isFindingUserByEmail}
                                            onChange={async (_, { value }) => {
                                                await onChange({
                                                    target: {
                                                        name,
                                                        value,
                                                    },
                                                });
                                                checkIfEmailAlreadyInUse({ name, value });
                                            }}
                                            fluid
                                            name="email"
                                            error={error?.message}
                                        />
                                    )}
                                />
                            }
                        />
                        <ListItem
                            icon={<Icon name="map marker" color="black" />}
                            header="Street Address"
                            description={
                                <Controller
                                    control={control}
                                    name="street"
                                    render={({
                                        field: { name, value, onChange },
                                        fieldState: { error },
                                    }) => (
                                        <Form.Input
                                            value={value}
                                            onChange={(_, { value }) => {
                                                onChange({
                                                    target: {
                                                        name,
                                                        value,
                                                    },
                                                });
                                            }}
                                            name="street"
                                            error={error?.message}
                                            placeholder="Street address or P.O. box"
                                            fluid
                                            required
                                        />
                                    )}
                                />
                            }
                        />
                        <ListItem
                            icon={<Icon />}
                            header="Apt. suite, unit building, floor, etc."
                            description={
                                <Controller
                                    control={control}
                                    name="suite"
                                    render={({
                                        field: { name, value, onChange },
                                        fieldState: { error },
                                    }) => (
                                        <Form.Input
                                            value={value}
                                            onChange={(_, { value }) => {
                                                onChange({
                                                    target: {
                                                        name,
                                                        value,
                                                    },
                                                });
                                            }}
                                            name="suite"
                                            error={error?.message}
                                            fluid
                                            required
                                        />
                                    )}
                                />
                            }
                        />
                        <ListItem
                            icon={<Icon />}
                            header="City"
                            description={
                                <Controller
                                    control={control}
                                    name="city"
                                    render={({
                                        field: { name, value, onChange },
                                        fieldState: { error },
                                    }) => (
                                        <Form.Input
                                            value={value}
                                            onChange={(_, { value }) => {
                                                onChange({
                                                    target: {
                                                        name,
                                                        value,
                                                    },
                                                });
                                            }}
                                            name="city"
                                            error={error?.message}
                                            placeholder="City"
                                            fluid
                                            required
                                        />
                                    )}
                                />
                            }
                        />
                        <ListItem
                            icon={<Icon />}
                            header={
                                <b>
                                    State{" "}
                                    <Icon
                                        size="tiny"
                                        name="asterisk"
                                        color="red"
                                    />
                                </b>
                            }
                            description={
                                <Grid>
                                    <Grid.Column width="8">
                                        <Controller
                                            control={control}
                                            name="state"
                                            render={({
                                                field: {
                                                    name,
                                                    value,
                                                    onChange,
                                                },
                                            }) => (
                                                <Dropdown
                                                    selection
                                                    search
                                                    fluid
                                                    onChange={(
                                                        _,
                                                        { value }
                                                    ) => {
                                                        onChange({
                                                            target: {
                                                                name,
                                                                value,
                                                            },
                                                        });
                                                    }}
                                                    value={value}
                                                    name="state"
                                                    required
                                                    options={STATES.map(
                                                        (state) => ({
                                                            ...state,
                                                            value: state.key,
                                                        })
                                                    )}
                                                    placeholder="Select a state"
                                                />
                                            )}
                                        />
                                    </Grid.Column>
                                    <Grid.Column
                                        width="8"
                                        style={{ paddingTop: 0 }}
                                    >
                                        <ListItem
                                            header="Zip Code"
                                            description={
                                                <Controller
                                                    control={control}
                                                    name="zipcode"
                                                    render={({
                                                        field: {
                                                            name,
                                                            value,
                                                            onChange,
                                                        },
                                                        fieldState: { error },
                                                    }) => (
                                                        <Form.Input
                                                            value={value}
                                                            onChange={(
                                                                _,
                                                                { value }
                                                            ) => {
                                                                onChange({
                                                                    target: {
                                                                        name,
                                                                        value,
                                                                    },
                                                                });
                                                            }}
                                                            name="zipcode"
                                                            error={
                                                                error?.message
                                                            }
                                                            placeholder="Postal Code"
                                                            fluid
                                                            required
                                                        />
                                                    )}
                                                />
                                            }
                                        />
                                    </Grid.Column>
                                </Grid>
                            }
                        />
                    </List>
                </Grid.Column>
                <Grid.Column>
                    <List relaxed size="small" as={Form}>
                        {canAddUserExtension && (
                            <ListItem
                                icon={<Icon name="fa-solid fa-circle-user" color="black" />}
                                header={
                                    <b>
                                        User Extension{" "}
                                        <Icon
                                            size="tiny"
                                            name="asterisk"
                                            color="red"
                                        />
                                    </b>
                                }
                                description={
                                    <Controller
                                        control={control}
                                        name="extension"
                                        render={({
                                            field: { name, value, onChange },
                                            fieldState: { error },
                                        }) => (
                                            <Form.Select
                                                name={name}
                                                value={value}
                                                onChange={(_, { value }) => { onChange({ target: { name, value } }) }}
                                                placeholder='Select User Extension'
                                                clearable
                                                options={unusedUserExtensions.map((extension) => ({
                                                    key: extension.extension_id,
                                                    value: extension.extension_id,
                                                    text: extension.extension
                                                }))}
                                                loading={isReadingUnusedUserExtensions}
                                                error={error?.message}
                                                required
                                            />
                                        )}
                                    />
                                }
                            />
                        )}
                        <ListItem
                            icon={<Icon name="text telephone" color="black" />}
                            header="Work Number"
                            description={
                                <Grid>
                                    <Grid.Column >
                                        <Controller
                                            control={control}
                                            name="work_number"
                                            render={({
                                                field: {
                                                    name,
                                                    value,
                                                    onChange,
                                                },
                                                fieldState: { error },
                                            }) => (
                                                <Form.Input
                                                    required
                                                    onChange={(
                                                        _,
                                                        { value }
                                                    ) => {
                                                        onChange({
                                                            target: {
                                                                name,
                                                                value,
                                                            },
                                                        });
                                                    }}
                                                    value={PhoneUtil.formatDisplayedValue(isFocused(name), value)}
                                                    onBlur={() => {
                                                        PhoneUtil.handlePhoneBlur(value, onChange, name, removeFocus);
                                                    }}
                                                    onPaste={e => PhoneUtil.handlePhonePaste(e, onChange, name)}
                                                    onFocus={() => {
                                                        addFocus(name)
                                                    }}
                                                    name="work_number"
                                                    fluid
                                                    error={error?.message}
                                                    placeholder="xxxxxxxxxx"
                                                />
                                            )}
                                        />
                                    </Grid.Column>
                                </Grid>
                            }
                        />
                        <ListItem
                            icon={
                                <Icon name="mobile alternate" color="black" />
                            }
                            header="Cell Number"
                            description={
                                <Controller
                                    control={control}
                                    name="cell_number"
                                    render={({
                                        field: { name, value, onChange },
                                        fieldState: { error },
                                    }) => (
                                        <Form.Input
                                            value={PhoneUtil.formatDisplayedValue(isFocused(name), value)}
                                            required
                                            onChange={(_, { value }) => {
                                                onChange({
                                                    target: {
                                                        name,
                                                        value,
                                                    },
                                                });
                                            }}
                                            onFocus={() => {
                                                addFocus(name)
                                            }}
                                            onPaste={e => PhoneUtil.handlePhonePaste(e, onChange, name)}
                                            onBlur={() => {
                                                PhoneUtil.handlePhoneBlur(value, onChange, name, removeFocus);
                                            }}
                                            name="cell_number"
                                            fluid
                                            error={error?.message}
                                            placeholder="xxxxxxxxxx"
                                        />
                                    )}
                                />
                            }
                        />
                        <ListItem
                            icon={<Icon name="clock outline" color="black" />}
                            header={
                                <b>
                                    Timezone{" "}
                                    <Icon
                                        size="tiny"
                                        name="asterisk"
                                        color="red"
                                    />
                                </b>
                            }
                            description={
                                <Controller
                                    control={control}
                                    name="timezone"
                                    render={({
                                        field: { name, value, onChange },
                                    }) => (
                                        <Select
                                            search
                                            value={value}
                                            options={DateTimeUtils.generateTimezoneOptions()}
                                            required
                                            onChange={(_, { value }) => {
                                                onChange({
                                                    target: { name, value },
                                                });
                                            }}
                                            name="timezone"
                                            fluid
                                            placeholder="Select Time Zone"
                                        />
                                    )}
                                />
                            }
                        />
                        <ListItem
                            icon={<Icon name="address book" color="black" />}
                            header={<b>Job Title</b>}
                            description={
                                <Controller
                                    control={control}
                                    name="job_title"
                                    render={({
                                        field: { name, value, onChange },
                                        fieldState: { error },
                                    }) => (
                                        <Form.Input
                                            value={value}
                                            onChange={(_, { value }) => {
                                                onChange({
                                                    target: { name, value },
                                                });
                                            }}
                                            name="job_title"
                                            fluid
                                            error={error?.message}
                                        />
                                    )}
                                />
                            }
                        />
                        <ListItem
                            icon={<Icon name="clipboard" color="black" />}
                            header="Reports to"
                            description={
                                <Controller
                                    control={control}
                                    name="report_to"
                                    render={({
                                        field: { name, value, onChange },
                                    }) => (
                                        <Form.Field
                                            control={UserSearchField}
                                            excluded_users={[]}
                                            onChange={(_, { value }) => {
                                                onChange({
                                                    target: { name, value },
                                                });
                                            }}
                                            placeholder="Select User"
                                            display_selected_user
                                            value={value}
                                            disabled={reportsToDisabled}
                                        />
                                    )}
                                />
                            }
                        />
                        <ListItem
                            icon={<Icon name="fas fa-users" color="black" />}
                            header="Team(s)"
                            description={
                                <Controller
                                    control={control}
                                    name={"teams"}
                                    render={({
                                        field: { name, value, onChange },
                                    }) => (
                                        <Dropdown
                                            multiple
                                            selection
                                            fluid
                                            onChange={(_, { value }) => {
                                                onChange({
                                                    target: { name, value },
                                                });
                                            }}
                                            name="teams"
                                            options={teams}
                                            loading={isReadingTeams}
                                            placeholder="Select Team(s)"
                                            value={value}
                                        />
                                    )}
                                />
                            }
                        />
                        <ListItem
                            icon={<Icon name="id card" color="black" />}
                            header={
                                <b>
                                    Profile Type{" "}
                                    <Icon
                                        size="tiny"
                                        name="asterisk"
                                        color="red"
                                    />
                                </b>
                            }
                            description={
                                <Controller
                                    control={control}
                                    name={"profile_type"}
                                    render={({
                                        field: { name, value, onChange },
                                        fieldState: { error },
                                    }) => (
                                        <Dropdown
                                            name="profile_type"
                                            value={value}
                                            fluid
                                            selection
                                            loading={isReadingRoles}
                                            required
                                            placeholder="Select Profile Type"
                                            text={roles.find(role => role.value === value)?.text || ""}
                                            error={error?.message}
                                        >
                                            <Dropdown.Menu>
                                                {roles.map(role => (
                                                    <RestrictedView type="hidden" productPermission={PRODUCT_PERMISSIONS.SETTINGS_USERS} accessRole={role.value}>
                                                        <Dropdown.Item
                                                            key={role.key}
                                                            text={role.text}
                                                            value={role.value}
                                                            active={role.value === value}
                                                            onClick={(_, { value: selectedValue }) => {
                                                                onChange({
                                                                    target: { name, value: selectedValue },
                                                                });
                                                            }}
                                                        />
                                                    </RestrictedView>
                                                ))}
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    )}
                                />
                            }
                        />
                    </List>
                </Grid.Column>
            </Grid.Row>
        </Grid>
    );
};

export default UserForm;
